// Mesajdaki link önizlemesinin iki görünümü (9.32 · D-653).
//
// KISA: logo, başlık ve sitenin gerçek adresi — ChatGPT'deki gibi küçük bir
// hap. BÜYÜK KART: görsel, başlık, kısa açıklama ve adres — WhatsApp ve
// Telegram'daki gibi.
//
// ADRES YALAN SÖYLEYEMEZ. Başlığı, logoyu ve görseli gönderenin telefonu
// getirdi; kötü niyetli biri banka adı ve logosu koyup başka bir siteye
// götürebilir. Adres ise burada, alıcının telefonunda, mesajdaki linkin
// kendisinden okunuyor. Yer daralınca başlık kısalır, adres hiç kısalmaz.
// "Gördüğün yer gittiğin yerdir" kuralı (D-295) böyle korunuyor; dokununca
// yine hangi siteye gidildiğini soran onay çıkıyor.
import React from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from './rn';
import Ionicons from '@expo/vector-icons/Ionicons';
import { fontSize, radius, spacing, ThemeColors } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { linkHost } from '../utils/links';
import type { LinkPreview } from '../linkPreview/payload';

type Props = {
  preview: LinkPreview;
  /** Balon gönderenin mi: renkler balonun zeminine göre seçiliyor. */
  mine: boolean;
  onPress: () => void;
  onLongPress?: () => void;
  testID?: string;
};

export function LinkPill({ preview, mine, onPress, onLongPress, testID }: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  const styles = makeStyles(colors);
  const host = linkHost(preview.url);
  const fg = mine ? colors.white : colors.black;
  return (
    <TouchableOpacity
      testID={testID}
      onPress={onPress}
      onLongPress={onLongPress}
      activeOpacity={0.7}
      accessibilityRole="link"
      accessibilityLabel={t('message.linkPreviewA11y', { title: preview.title ?? host, host })}
      style={[styles.pill, mine ? styles.pillMine : styles.pillOther]}>
      {preview.icon
        ? <Image source={{ uri: preview.icon }} style={styles.pillIcon} />
        : <Ionicons name="link-outline" size={15} color={fg} />}
      {preview.title ? (
        <Text numberOfLines={1} style={[styles.pillTitle, { color: fg }]}>{preview.title}</Text>
      ) : null}
      <Text
        testID={testID ? `${testID}-host` : undefined}
        numberOfLines={1}
        // Çok uzun bir adres ancak BAŞINDAN kısalır: asıl alan adı sondadır.
        ellipsizeMode="head"
        style={[styles.pillHost, { color: mine ? colors.white : colors.gray600 }]}>
        {preview.title ? `· ${host}` : host}
      </Text>
    </TouchableOpacity>
  );
}

export function LinkCard({ preview, mine, onPress, onLongPress, testID }: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  const styles = makeStyles(colors);
  const host = linkHost(preview.url);
  const fg = mine ? colors.white : colors.black;
  return (
    <TouchableOpacity
      testID={testID}
      onPress={onPress}
      onLongPress={onLongPress}
      activeOpacity={0.8}
      accessibilityRole="link"
      accessibilityLabel={t('message.linkPreviewA11y', { title: preview.title ?? host, host })}
      style={[styles.card, mine ? styles.cardMine : styles.cardOther]}>
      {preview.image ? <Image source={{ uri: preview.image }} style={styles.cardImage} resizeMode="cover" /> : null}
      <View style={styles.cardBody}>
        {preview.title ? (
          <Text numberOfLines={2} style={[styles.cardTitle, { color: fg }]}>{preview.title}</Text>
        ) : null}
        {preview.description ? (
          <Text numberOfLines={2} style={[styles.cardDescription, { color: fg }]}>{preview.description}</Text>
        ) : null}
        <View style={styles.cardHostRow}>
          {preview.icon
            ? <Image source={{ uri: preview.icon }} style={styles.cardIcon} />
            : <Ionicons name="link-outline" size={13} color={fg} />}
          <Text
            testID={testID ? `${testID}-host` : undefined}
            numberOfLines={1}
            ellipsizeMode="head"
            style={[styles.cardHost, { color: mine ? colors.white : colors.gray600 }]}>
            {host}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );
}

// Balonun zemini sende koyu, karşıda açık (koyu temada tersi). İki taraf için de
// zeminden hafif ayrışan, iki temada da çalışan yarı saydam bir ton.
const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    maxWidth: '100%',
    gap: 6,
    marginTop: spacing.xs,
    paddingVertical: 5,
    paddingLeft: 7,
    paddingRight: 10,
    borderRadius: 999,
  },
  pillMine: { backgroundColor: 'rgba(128,128,128,0.28)' },
  pillOther: { backgroundColor: colors.white },
  pillIcon: { width: 16, height: 16, borderRadius: 4 },
  pillTitle: { fontSize: fontSize.sm, flexShrink: 1 },
  // Adres hiç kısalmaz: yer daralınca kısalan başlık.
  pillHost: { fontSize: fontSize.sm, flexShrink: 0, maxWidth: '100%', opacity: 0.8 },
  card: { width: 260, maxWidth: '100%', borderRadius: radius.control, overflow: 'hidden', marginBottom: spacing.xs },
  cardMine: { backgroundColor: 'rgba(128,128,128,0.22)' },
  cardOther: { backgroundColor: colors.white },
  cardImage: { width: '100%', aspectRatio: 480 / 252 },
  cardBody: { paddingHorizontal: 10, paddingVertical: 8, gap: 2 },
  cardTitle: { fontSize: fontSize.sm, fontWeight: '600' },
  cardDescription: { fontSize: 13, opacity: 0.85 },
  cardHostRow: { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 3 },
  cardIcon: { width: 13, height: 13, borderRadius: 2 },
  cardHost: { fontSize: 12, flexShrink: 1, opacity: 0.8 },
});
